import React, { useMemo, useState } from 'react';
import { FiX, FiStar } from 'react-icons/fi';
import { SOCIAL_SCHEME_LINES, SOCIAL_SCHEME_SECTIONS } from '../../constants/config';
import { resolveImage } from '../../utilities/infographics';
import '../../styles/editModal.css';
import '../../styles/infographics.css';

// Сборная тура. Перенос TeamChoose из приложения: схема задаёт число слотов
// в каждой линии, у слота есть index вида "<линия>_<позиция>" — по нему сервер
// расставляет игроков на картинке. Вратарь всегда один, index "4_1".
//props:
// open, onClose
// scheme — id схемы (activeScheme)
// players — уже собранный состав (activePlayers)
// onPick — (массив игроков) при сохранении
// onOpenPlayerPicker — (banIds, callback) открыть выбор игрока у родителя

export default function TourTeamEditor({
    open, onClose, scheme, players, onPick, onOpenPlayerPicker,
}) {
    const [draft, setDraft] = useState([]);
    const [ready, setReady] = useState(false);

    // Слоты пересобираем под схему и накладываем то, что уже выбрано
    const slots = useMemo(() => {
        const lines = SOCIAL_SCHEME_LINES[Number(scheme)];
        if (!lines) return [];
        const current = ready ? draft : (Array.isArray(players) ? players : []);
        const sections = SOCIAL_SCHEME_SECTIONS.map((section, sectionIndex) => {
            const count = lines[sectionIndex] || 0;
            const rows = [];
            for (let i = 0; i < count; i++) {
                const index = `${section.line}_${i + 1}`;
                const filled = current.find(one => one.index === index);
                rows.push(filled || {
                    index,
                    position_name: `${section.prefix}${i + 1}`,
                });
            }
            return { title: section.title, rows };
        });
        const keeperIndex = '4_1';
        const keeper = current.find(one => one.index === keeperIndex) || {
            index: keeperIndex,
            position_name: 'ВРТ',
        };
        sections.push({ title: 'Вратарь', rows: [keeper] });
        return sections;
    }, [scheme, players, draft, ready]);

    if (!open) return null;

    const flat = slots.reduce((acc, section) => acc.concat(section.rows), []);
    const bannedIds = flat.filter(one => one.player_id).map(one => one.player_id);

    const assign = (slot, player) => {
        const next = flat.map(one => (
            one.index === slot.index
                ? {
                    ...one,
                    player_id: player.player_id || player.id,
                    family: player.family,
                    name: player.name,
                    image: player.player_image || player.image,
                    team_id: player.team_id,
                    team_image: player.team_image,
                    team_name: player.team_name,
                }
                : one
        ));
        setDraft(next);
        setReady(true);
    };

    const clearSlot = (slot) => {
        const next = flat.map(one => (
            one.index === slot.index
                ? { index: one.index, position_name: one.position_name }
                : one
        ));
        setDraft(next);
        setReady(true);
    };

    // Звезда помечает лучшего игрока — в приложении это switchStar
    const toggleStar = (slot) => {
        const next = flat.map(one => (
            one.index === slot.index ? { ...one, star: !one.star } : one
        ));
        setDraft(next);
        setReady(true);
    };

    const save = () => {
        onPick(flat.filter(one => one.player_id));
        onClose();
    };

    return (
        <div className="modalOverlay" onClick={onClose}>
            <div className="modalContent modalContentWide" onClick={(e) => e.stopPropagation()}>
                <div className="modalHeader">
                    <h2>Сборная тура</h2>
                    <button className="modalClose" onClick={onClose}>×</button>
                </div>

                <div className="modalBody modalBodyScroll">
                    {slots.length === 0 ? (
                        <p className="igControlNote">
                            Сначала выберите схему расстановки — от неё зависит,
                            сколько игроков в каждой линии.
                        </p>
                    ) : slots.map(section => (
                        <div className="igSlotSection" key={section.title}>
                            <h4 className="igSlotSectionTitle">{section.title}</h4>
                            {section.rows.map(slot => (
                                <div className="igSlotRow" key={slot.index}>
                                    <span className="igSlotPosition">{slot.position_name}</span>
                                    <button
                                        type="button"
                                        className="igSlotPicker"
                                        onClick={() => onOpenPlayerPicker(
                                            bannedIds.filter(id => id !== slot.player_id),
                                            (player) => assign(slot, player),
                                        )}
                                    >
                                        {slot.player_id ? (
                                            <>
                                                {resolveImage(slot.image) ? (
                                                    <img src={resolveImage(slot.image)} alt="" />
                                                ) : null}
                                                <span className="igSlotName">
                                                    {`${slot.family || ''} ${slot.name || ''}`.trim()}
                                                </span>
                                                <span className="igListRowMeta">{slot.team_name}</span>
                                            </>
                                        ) : (
                                            <span className="igPickerPlaceholder">Выбрать игрока</span>
                                        )}
                                    </button>
                                    {slot.player_id ? (
                                        <>
                                            <button
                                                type="button"
                                                className={`igSlotIconBtn ${slot.star ? 'igSlotIconBtnActive' : ''}`}
                                                title="Лучший игрок"
                                                onClick={() => toggleStar(slot)}
                                            >
                                                <FiStar />
                                            </button>
                                            <button
                                                type="button"
                                                className="igSlotIconBtn"
                                                title="Очистить"
                                                onClick={() => clearSlot(slot)}
                                            >
                                                <FiX />
                                            </button>
                                        </>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div className="modalFooter">
                    <span className="igControlHint">
                        Заполнено: {flat.filter(one => one.player_id).length} из {flat.length}
                    </span>
                    <button className="igPrimaryBtn" disabled={slots.length === 0} onClick={save}>
                        Применить
                    </button>
                </div>
            </div>
        </div>
    );
}
